import { Injectable } from '@angular/core';
import { Overlay } from '@angular/cdk/overlay';
import { hasStoredAccessToken } from '../../user/auth-tokens';
import { MapModalData } from '../../user/map-redirect-modal/map-redirect-modal.component';
import { SubscriptionPlans } from '../../user/user/user.model';
import { BackendRegionType } from '../../user/subscriptions.service';
import { CoverageAreaModalData } from '../components/coverage-area-modal/coverage-area-modal.component';
import { MatDialog, MatDialogRef, MatDialogState } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import { modalRegistry } from './modal-registry';
import { CATEGORY_TO_PLAN, USER_CATEGORY_KEY, UserCategory } from '../../map/user-guide/user-guide.service';
import { LEVEL_LAYER } from '../../map/mapbox/services/layer-store.service';
import { BreakpointObserverService } from './breakpoint-observer.service';
import { SignUpTrackingService } from '../../user/sign-up-tracking.service';
import { SubscriptionsMenuComponent } from '../../user/user/subscriptions-menu/subscriptions-menu.component';
import type { HelpAndFeedbackModalData } from '../components/help-and-feedback-modal/help-and-feedback-modal.component';

@Injectable({
  providedIn: 'root',
})
export class ModalService {
  public isModalOpen = false;

  constructor(
    private dialog: MatDialog,
    private breakpointObserver: BreakpointObserverService,
    private overlay: Overlay,
    private signUpTracking: SignUpTrackingService
  ) {}

  public async openModal(text: MapModalData): Promise<void> {
    // Check if modal is already opened otherwise it could open in multiple instances
    if (!this.isModalOpen) {
      this.isModalOpen = true; // Set the status to open
      const dialogRef = this.dialog.open(
        await modalRegistry.MapRedirectModal(),
        {
          panelClass: 'custom-dialog-container',
          disableClose: text.disableClose,
          hasBackdrop: text.hasBackdrop,
          maxWidth: '620px',
          data: text,
        }
      );

      dialogRef.afterClosed().subscribe(() => {
        this.isModalOpen = false;
      });
    }
  }

  public async openHelpAndFeedbackModal(initialMessage?: string): Promise<void> {
    this.dialog.open(await modalRegistry.HelpAndFeedbackModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: `${Math.min(800, window.innerWidth - 32)}px`,
      // The default block strategy fixes <html> at top: -scrollY, which
      // double-shifts scrolled pages here because the global
      // `html { overflow-x: hidden }` makes <html> its own scroll container
      // (it keeps its scrollTop AND gets the offset). Skip scroll blocking —
      // the backdrop already prevents interaction with the page.
      scrollStrategy: this.overlay.scrollStrategies.noop(),
      data: { message: initialMessage } as HelpAndFeedbackModalData,
    });
  }


  public closeGenericModal(): void {
    if (this.isModalOpen) {
      this.dialog.closeAll();
      this.isModalOpen = false;
    }
  }

  public async handleSubscriptionConfirmationModal(
    plan: SubscriptionPlans,
    isPeriodMonthly: boolean,
    freePeriodString: string | null,
    promoCode: string | null,
    regionType?: BackendRegionType,
    regionsIdsCommaSeparated?: string,
    regionNames?: string[],
    coverageData?: CoverageAreaModalData,
  ): Promise<void> {
    const ref = this.dialog.open(await modalRegistry.SubscriptionConfirmationModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '620px',
      data: {
        plan,
        isPeriodMonthly,
        freePeriodString,
        promoCode,
        regionType,
        regionsIdsCommaSeparated,
        regionNames,
      },
    });

    // When coverage modal led to this confirmation, reopen it on cancel so the
    // user can adjust their region selection. localStorage restores the state.
    if (coverageData) {
      ref.afterClosed().subscribe((result) => {
        if (!result) {
          this.openCoverageAreaModal(
            coverageData.plan,
            coverageData.regionalPricing,
            coverageData.promoCode,
            coverageData.freePeriodString,
          );
        }
      });
    }
  }

  private pendingAuthRequest: Promise<boolean> | null = null;

  /**
   * Opens the in-place sign-up / sign-in modal on top of whatever is
   * currently open (purchase flow, map paywalls, AI chat) so anonymous
   * users don't lose their context. Resolves once the modal is closed,
   * with the actual auth state at that moment — regardless of how the
   * modal was closed.
   *
   * Paywall triggers fire repeatedly (map rerenders, clicks), so while
   * an auth modal is already open all callers share it instead of
   * stacking new ones.
   */
  public requestAuth(options?: {
    headerText?: string;
    subHeaderText?: string;
    initialMode?: 'options' | 'signup' | 'signin';
    /**
     * Which paywall made the visitor sign up, reported with the `sign_up`
     * event. Only known here: by the time the account exists, the gate that
     * demanded it is closed and forgotten.
     */
    placement?: string;
  }): Promise<boolean> {
    if (this.pendingAuthRequest) {
      return this.pendingAuthRequest;
    }

    this.pendingAuthRequest = (async () => {
      try {
        if (options?.placement) {
          this.signUpTracking.gateOpened(options.placement);
        }
        const ref = this.dialog.open(await modalRegistry.AuthModal(), {
          panelClass: 'custom-dialog-container',
          disableClose: false,
          maxWidth: '600px',
          autoFocus: false,
          data: options ?? null,
        });
        await firstValueFrom(ref.afterClosed());
        return hasStoredAccessToken();
      } finally {
        this.signUpTracking.gateClosed();
        this.pendingAuthRequest = null;
      }
    })();

    return this.pendingAuthRequest;
  }

  private coverageAreaModal?: MatDialogRef<unknown>;

  /**
   * @param onSubscribeClick notified with the chosen coverage tier when the
   *   visitor commits, before the sign-up gate. Lets a caller that tracks its
   *   own funnel (the sales-forecast pages) observe this step without the
   *   shared modal having to import that feature's analytics service - the
   *   other entry points (profile, /pricing, the recommendation modal) simply
   *   don't pass it.
   */
  public async openCoverageAreaModal(
    plan: SubscriptionPlans,
    regionalPricing: { county: string; state: string; fullUs: string },
    promoCode: string | null,
    freePeriodString: string | null,
    subtitle?: string,
    onSubscribeClick?: (coverageTier: string) => void,
  ): Promise<void> {
    this.coverageAreaModal = this.dialog.open(await modalRegistry.CoverageAreaModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '720px',
      data: { plan, regionalPricing, promoCode, freePeriodString, subtitle, onSubscribeClick },
      // Same fix as openHelpAndFeedbackModal above: the default block
      // strategy fixes <html> at top: -scrollY, which double-shifts
      // scrolled pages because the global `html { overflow-x: hidden }`
      // makes <html> its own scroll container (it keeps its scrollTop AND
      // gets the offset) — on a long scrolled page (e.g. sales-forecast)
      // this pushes all content off-screen while the dialog is open.
      scrollStrategy: this.overlay.scrollStrategies.noop(),
    });
  }

  private isCoverageAreaModalOpen(): boolean {
    return this.coverageAreaModal?.getState() === MatDialogState.OPEN;
  }

  public async handleSubscriptionRenewalModal(
    isAutoRenewable: boolean
  ): Promise<void> {
    this.dialog.open(await modalRegistry.SubscriptionRenewalModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '620px',
      data: {
        isAutoRenewable: isAutoRenewable,
      },
    });
  }

  public async handleFavoriteCellPersonalizationModal(
    id: string,
    title: string,
    level: LEVEL_LAYER
  ): Promise<void> {
    this.dialog.open(await modalRegistry.FavoriteCellPersonalizationModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '620px',
      maxHeight: '510px',
      data: { id, title: title, level: level },
    });
  }

  public async handleSetRadiusModal(radius: number): Promise<void> {
    this.dialog.open(await modalRegistry.SetRadiusModal(), {
      panelClass: 'custom-dialog-container',
      disableClose: false,
      maxWidth: '560px',
      maxHeight: '275px',
      data: { radius: radius },
    });
  }

  private subscriptionsModal?: MatDialogRef<SubscriptionsMenuComponent>;
  // Guards the async gap while the modal chunk is being lazy-loaded:
  // the getState() check below can't see a dialog that isn't open yet,
  // and paywall triggers fire repeatedly (map rerenders, clicks)
  private isSubscriptionsModalOpening = false;

  public closeSubscriptionsModal(): void {
    this.subscriptionsModal?.close();
  }

  public async openSubscriptionsModal(mode: "all_packages" | "ai_packages"): Promise<void> {
    if (
      this.isSubscriptionsModalOpening ||
      (this.subscriptionsModal &&
        this.subscriptionsModal.getState() === MatDialogState.OPEN)
    ) {
      return;
    }

    // User is already choosing a coverage area (deeper in the purchase flow) —
    // don't stack the subscription upsell on top of it
    if (this.isCoverageAreaModalOpen()) {
      return;
    }

    this.isSubscriptionsModalOpening = true;
    try {
      const category = localStorage.getItem(USER_CATEGORY_KEY) as UserCategory | null;
      const recommendedPlan = category ? CATEGORY_TO_PLAN[category] : undefined;

      if (recommendedPlan) {
        const res = this.dialog.open(await modalRegistry.RecommendedSubscriptionModal(), {
          panelClass: 'custom-dialog-container',
          disableClose: false,
          maxWidth: '600px',
          data: { recommendedPlan, mode },
        });
        this.subscriptionsModal = res as any;
        return;
      }

      const isMobile = this.breakpointObserver.isMobile.value;
      const res = this.dialog.open(await modalRegistry.SubscriptionPlansModal(), {
        panelClass: 'custom-dialog-container',
        disableClose: false,
        maxWidth: isMobile ? '800px' : '1200px',
        maxHeight: isMobile ? '1200px' : '900px',
        data: { mode },
      });
      this.subscriptionsModal = res;
    } finally {
      this.isSubscriptionsModalOpening = false;
    }
  }
}
